/**
 * CONSAI - Invoice Item Row Component
 * Handles individual item input with searchable dropdown
 */
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Search, X } from 'lucide-react';

// Common GST rates
const GST_RATES = [0, 5, 12, 18, 28];

// Unit options
const UNIT_OPTIONS = ['pcs', 'kg', 'g', 'l', 'ml', 'box', 'pack', 'dozen', 'meter', 'sq.ft', 'hour', 'day'];

const ItemRow = ({
  item,
  index,
  onUpdateItem,
  onRemoveItem,
  canRemove,
  inventoryItems,
  onItemSelect,
  showItemDropdown,
  setShowItemDropdown,
  itemSearchQuery,
  setItemSearchQuery,
}) => {
  const { language } = useLanguage();

  // Filter inventory items based on search
  const filteredInventoryItems = inventoryItems.filter(invItem =>
    invItem.item_name?.toLowerCase().includes(itemSearchQuery.toLowerCase())
  );

  // Calculate item total with GST
  const itemTotal = (Number(item.quantity) * Number(item.unit_price)) * (1 + Number(item.gst_percent) / 100);

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-white">
      <div className="flex items-start gap-2">
        <span className="text-xs text-slate-400 mt-2">{index + 1}.</span>
        <div className="flex-1 space-y-3">
          {/* Description with searchable dropdown */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 relative">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={language === 'hi' ? 'आइटम खोजें या नया दर्ज करें' : 'Search item or enter new'}
                  value={item.description}
                  onChange={(e) => {
                    onUpdateItem(index, 'description', e.target.value);
                    setItemSearchQuery(e.target.value);
                    setShowItemDropdown(index);
                  }}
                  onFocus={() => {
                    setShowItemDropdown(index);
                    setItemSearchQuery(item.description);
                  }}
                  className="text-sm pl-8"
                  data-testid={`item-description-${index}`}
                />
              </div>
              {/* Dropdown for inventory items */}
              {showItemDropdown === index && filteredInventoryItems.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredInventoryItems.slice(0, 10).map(invItem => (
                    <button
                      key={invItem.item_id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm flex justify-between items-center"
                      onClick={() => onItemSelect(index, invItem)}
                    >
                      <span>{invItem.item_name}</span>
                      <span className="text-slate-400 text-xs">Rs.{invItem.selling_price}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Input
                placeholder="HSN/SAC"
                value={item.hsn_sac}
                onChange={(e) => onUpdateItem(index, 'hsn_sac', e.target.value)}
                className="text-sm"
                data-testid={`item-hsn-${index}`}
              />
            </div>
          </div>

          {/* Qty, Unit, Rate, GST */}
          <div className="grid grid-cols-5 gap-2">
            <div>
              <Label className="text-xs text-slate-400">{language === 'hi' ? 'मात्रा' : 'Qty'}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={item.quantity}
                onChange={(e) => onUpdateItem(index, 'quantity', e.target.value)}
                className="text-sm"
                data-testid={`item-qty-${index}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">{language === 'hi' ? 'इकाई' : 'Unit'}</Label>
              <Select value={item.unit} onValueChange={(v) => onUpdateItem(index, 'unit', v)}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map(u => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">{language === 'hi' ? 'दर (Rs.)' : 'Rate (Rs.)'}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={item.unit_price}
                onChange={(e) => onUpdateItem(index, 'unit_price', e.target.value)}
                className="text-sm"
                data-testid={`item-rate-${index}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">GST %</Label>
              <Select value={String(item.gst_percent)} onValueChange={(v) => onUpdateItem(index, 'gst_percent', Number(v))}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GST_RATES.map(r => (
                    <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">{language === 'hi' ? 'कुल' : 'Total'}</Label>
              <div className="h-9 px-3 py-2 bg-slate-100 rounded-md text-sm font-medium text-slate-700">
                Rs.{itemTotal.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveItem(index)} className="mt-2">
            <X className="h-4 w-4 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ItemRow;
